import { useState, useEffect } from "react";
import Map from "../maps";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useAuth } from "../../core/auth";
import { Store } from "../../__generated__/graphql";
import { NoStore } from "./components/no-store";
import { HomeStoreList } from "./components/home-store-list";
import { useCart } from "../../core/cart";
import { track } from "@amplitude/analytics-browser";
import { SkeletonWave } from "../../components/common/skeleton-wave";
import BottomTab from "../../components/common/bottom-tab";
import AppDownloadBottomSheet from "../../components/app-download/app-download-bottom-sheet";
import { isWeb, isApp, isDev, isAndroidApp } from "../../utils/user-agent";
import ScrollRestore from "../../components/common/scroll-restore";
import AppDownloadBanner from "./components/app-download-banner";
import LocationAndCart from "./components/location-and-cart";
import TabSelector from "./components/tab-selector";
import BannerTop from "./components/banner-top";
import { HomeNewStoresSection } from "./components/home-new-stores-section";
import SearchBar from "./components/search-bar";
import FrequencyCountChip from "../../components/frequency/frequency-count-chip";
import { useElementViewCountTracking } from "./hooks/use-element-view-count-tracking";
import { useGuestMode } from "../../hooks/use-guest-mode";
import { usePickupTimeFilter } from "./hooks/use-pickup-time-filter";
import { useHomeQueryParams } from "./hooks/use-home-query-params";
import { useHomeRestoredStores } from "./hooks/use-home-restored-stores";
import { useHomeStoreList } from "./hooks/use-home-store-list";
import { useStoreLikeMutations } from "./hooks/use-store-like-mutations";
import { useFirstVisitModal } from "./hooks/use-first-visit-modal";
import { useNewStoreModal } from "./hooks/use-new-store-modal";
import { usePickupNowStores } from "./hooks/use-pickup-now-stores";
import { processAvailableFilter } from "./utils/store-filter";
import { HiExclamationCircle } from "react-icons/hi2";
import FirstVisitBottomSheet from "../../components/first-visit-bottom-sheet/first-visit-bottom-sheet";
import { postMessage } from "../../core/message";
import WelcomeStampBanner from "./components/welcome-stamp-banner";
import PickupTimeFilterBottomSheet from "./components/pickup-time-filter-bottom-sheet";
import StoreFilterChips from "./components/store-filter-chips";
import { useFirstPurchaseEvent } from "../../hooks/use-first-purchase-event";
import FirstPurchaseEventCard from "./components/first-purchase-event-card";
import FirstPurchaseCouponModal from "./components/first-purchase-coupon-modal";
import Modal from "../../components/common/modal";
import TodayPendingPackOrderInfoBar from "./components/today-pending-pack-order-info-bar";

import { NewStoreNotificationModal } from "./components/new-store-notification-modal";
import { FREQUENCY_COUNT_QUERY } from "../../api/frequency-queries";
import { GET_NEW_STORES } from "./api/home-queries";

const Home = () => {
  const {
    latNum,
    lngNum,
    loadAddr,
    firstVisit,
    currentLocationKey,
    hasLastLo,
    getLocationFromStorage,
  } = useHomeQueryParams();
  const restoredData = useHomeRestoredStores(currentLocationKey);
  const { likeStore, cancelLike } = useStoreLikeMutations();

  const { cart } = useCart();
  const { isLoggedIn } = useAuth();
  const { isGuestWithSource } = useGuestMode();
  const [isList, setIsList] = useState(
    sessionStorage.getItem("viewType")
      ? sessionStorage.getItem("viewType") === "list"
      : true,
  );
  const [showBanner, setShowBanner] = useState(isWeb());
  const [showWelcomeStampBanner, setShowWelcomeStampBanner] = useState(() => {
    return localStorage.getItem("welcomeStampBannerClosed") !== "true";
  });
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const [availableFilter, setAvailableFilter] = useState(
    processAvailableFilter(),
  );

  const { showFirstVisitModal, handleCloseFirstVisitModal } =
    useFirstVisitModal(firstVisit);

  const {
    isEligible: isFirstPurchaseEligible,
    markAsSeen: markFirstPurchaseSeen,
    issueCoupon: issueFirstPurchaseCoupon,
  } = useFirstPurchaseEvent();
  const [isFirstPurchaseModalOpen, setIsFirstPurchaseModalOpen] =
    useState(false);
  const [showCloseWarning, setShowCloseWarning] = useState(false);

  const {
    isNewStoreModalOpen,
    newStoresForModal,
    handleCloseNewStoreModal,
    handleGoToNewStore,
  } = useNewStoreModal({
    lat: latNum,
    lng: lngNum,
    showFirstVisitModal,
    isFirstPurchaseModalOpen,
    isSheetOpen,
  });

  const {
    pickupNowStores,
    isLoadingPickupNow,
    pickupNowCount,
    hasPickupNowStores,
  } = usePickupNowStores({
    lat: latNum,
    lng: lngNum,
    availableFilter,
    setAvailableFilter,
  });

  // 픽업 시간 필터 상태
  const [isPickupTimeFilterOpen, setIsPickupTimeFilterOpen] = useState(false);
  const {
    pickupTimeFilter,
    hasOpenedPickupTimeFilter,
    applyFilter,
    resetFilter,
    handleFilterOpen,
    initialStartTime,
    initialEndTime,
    hasFilter,
  } = usePickupTimeFilter();

  // 스토어 카드 뷰 추적 훅 (5개 본 후 다운로드 바텀시트 표시)
  const { attachObserver } = useElementViewCountTracking({
    threshold: 5,
    onTrigger: () => {
      setIsSheetOpen(true);
    },
    intersectionThreshold: 1,
    enabled: isWeb(), // 웹 환경에서만 활성화
    enableSessionPersistence: true, // 세션 유지 활성화
    sessionStorageKey: "home-download-sheet-shown", // 홈 전용 키
  });

  const { data: frequencyCountData } = useQuery(FREQUENCY_COUNT_QUERY, {
    skip: !isLoggedIn,
  });

  const BANNER_HEIGHT = 64; // 배너 높이 (px)

  const navigate = useNavigate();

  const limit = 20;

  const {
    rootRef,
    loadMoreRef,
    filteredStores,
    hasMore,
    isLoadingMore,
    initLoading,
    hasStores,
  } = useHomeStoreList({
    restoredData,
    latNum,
    lngNum,
    hasLastLo,
    getLocationFromStorage,
    loadAddr,
    limit,
    isList,
    availableFilter,
    pickupNowStores,
    pickupTimeFilter,
  });

  useEffect(() => {
    if (hasStores) {
      setTimeout(() => setIsRendered(true), 0);
    } else {
      setIsRendered(false);
    }
  }, [hasStores]);

  function onClickTab(type: string) {
    postMessage("REQ_HAPTIC_LIGHT", "");
    if (type === "LIST") {
      setIsList(true);
      sessionStorage.setItem("viewType", "list");
    } else {
      track("지도로 찾아보기 탭 클릭");
      setIsList(false);
      sessionStorage.setItem("viewType", "map");
    }
  }
  async function onClickLike(
    storeId: number,
    isLiked: boolean | null,
    storeTitle?: string,
  ) {
    postMessage("REQ_HAPTIC_LIGHT", "");
    if (isWeb()) {
      setIsSheetOpen(true);
      return;
    }
    track(isLiked ? "좋아요취소 클릭" : "좋아요 클릭", {
      매장명: storeTitle,
    });
    // 토글처리
    if (isLiked) {
      cancelLike({
        variables: {
          storeId,
        },
      });
    } else {
      likeStore({
        variables: {
          storeId,
        },
      });
    }
  }

  function onClickStore(store: Store, type: string) {
    postMessage("REQ_HAPTIC_LIGHT", "");
    track("매장 클릭", {
      매장명: store.title,
      유형: type,
    });

    navigate(`store/${store.id}`);
  }
  function onClickLocation() {
    navigate("/location");
  }
  function onClickCart() {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }
    if (cart.length === 0) {
      navigate("/live-order/checkout");
      return;
    }
    navigate(
      `/live-order/intro/${cart[0]?.store?.id}?closingHours=${encodeURIComponent(cart[0]?.store?.closingHours ?? "")}`,
    );
  }
  function onClickFavorite() {
    navigate("/like");
  }
  function onClickExplore() {
    track("검색 탭 클릭");
    navigate("/search");
  }

  const closeDownloadBanner = () => {
    setShowBanner(false);
  };
  const handleCloseSheet = () => {
    setIsSheetOpen(false);
  };

  // 신규입점매장 조회
  const { data: newStoresData } = useQuery(GET_NEW_STORES, {
    variables: { lat: latNum, lng: lngNum },
    skip: latNum == null || lngNum == null,
  });

  const newStores = newStoresData?.storesV2NewStores || [];

  return (
    <div className="flex h-[100dvh] min-h-[100dvh] w-full flex-col bg-white">
      {/* 앱 다운로드 배너 */}
      {showBanner && (
        <AppDownloadBanner
          closeBanner={closeDownloadBanner}
          bannerHeight={BANNER_HEIGHT}
        />
      )}
      <div
        className="flex h-full min-h-0 w-full flex-1 flex-col bg-white"
        style={{
          paddingTop: showBanner ? `${BANNER_HEIGHT}px` : "0", // 배너 높이만큼 패딩 추가
        }}
      >
        {/* LOCATION & CART */}
        <LocationAndCart
          onClickLocation={onClickLocation}
          onClickCart={onClickCart}
          cartLength={cart.length}
          onClickFavorite={onClickFavorite}
          onClickExplore={onClickExplore}
        />

        <>
          <div className="h-[0.0625rem] w-full bg-[#7a7a7a]" />
          <TabSelector isList={isList} onClickTab={onClickTab} />
          <div className="h-[0.0625rem] w-full bg-[#F4F5F7]" />
        </>

        {/* 웰컴 스탬프 배너 - 리스트 탭만 (지도 탭에서는 화면 절약) */}
        {(isApp() || isDev()) &&
          !isLoggedIn &&
          showWelcomeStampBanner &&
          isList && (
          <WelcomeStampBanner
            onClose={() => {
              localStorage.setItem("welcomeStampBannerClosed", "true");
              setShowWelcomeStampBanner(false);
            }}
          />
        )}

        {/* 첫 구매 이벤트 카드 - sticky */}
        {isLoggedIn && isFirstPurchaseEligible && (
          <FirstPurchaseEventCard
            onClose={() => setShowCloseWarning(true)}
            onClick={() => {
              track("첫 구매 이벤트 카드 클릭");
              setIsFirstPurchaseModalOpen(true);
            }}
          />
        )}

        {/* 확정대기 예약 주문 인포 바 */}
        <TodayPendingPackOrderInfoBar />

        <div
          className={`flex min-h-0 flex-1 flex-col overflow-hidden ${
            isAndroidApp()
              ? "pb-16"
              : "pb-[calc(4rem+env(safe-area-inset-bottom,0px))]"
          }`}
        >
          {!isList ? (
            <div className="relative flex min-h-[max(12.75rem,30svh)] flex-1 basis-0 flex-col">
              <Map
                availableFilter={availableFilter}
                pickupTimeFilter={pickupTimeFilter}
                topOverlay={
                  <StoreFilterChips
                    variant="availabilityOnly"
                    pickupTimeFilter={pickupTimeFilter}
                    hasOpenedPickupTimeFilter={hasOpenedPickupTimeFilter}
                    onOpenPickupTimeFilter={() =>
                      setIsPickupTimeFilterOpen(true)
                    }
                    onResetPickupTimeFilter={resetFilter}
                    availableFilter={availableFilter}
                    setAvailableFilter={setAvailableFilter}
                    hasPickupNowStores={hasPickupNowStores}
                    pickupNowCount={pickupNowCount}
                  />
                }
              />
            </div>
          ) : (
            <ScrollRestore
              id="scroll-home"
              initRestore={isRendered}
              showScrollTopButton
              ref={rootRef}
              className="min-h-0 flex-1"
            >
              <div className="flex justify-between items-end gap-3 px-4 pt-3 pb-3">
                <FrequencyCountChip
                  count={
                    isLoggedIn
                      ? frequencyCountData?.me?.frequencyCount || 0
                      : isGuestWithSource
                        ? 2
                        : 0
                  }
                  onClick={() => {
                    track("프리퀀시 진입로 클릭", { 어디서: "홈" });
                    navigate("/frequency");
                  }}
                />

                <SearchBar />
              </div>

              <BannerTop />
              {initLoading ? (
                <SkeletonWave className="pt-8 px-4 z-0" />
              ) : null}

              <HomeNewStoresSection
                newStores={newStores.filter(
                  (s: Store | null): s is Store => s != null,
                )}
                onClickLike={onClickLike}
                onOpenSheet={(e) => {
                  e.stopPropagation();
                  setIsSheetOpen(true);
                }}
                onClickStore={onClickStore}
                navigate={navigate}
              />

              <StoreFilterChips
                pickupTimeFilter={pickupTimeFilter}
                hasOpenedPickupTimeFilter={hasOpenedPickupTimeFilter}
                onOpenPickupTimeFilter={() => setIsPickupTimeFilterOpen(true)}
                onResetPickupTimeFilter={resetFilter}
                availableFilter={availableFilter}
                setAvailableFilter={setAvailableFilter}
                hasPickupNowStores={hasPickupNowStores}
                pickupNowCount={pickupNowCount}
              />

              <div className="h-[0.0625rem] w-full bg-[#F4F5F7] mt-2" />

              {isLoadingPickupNow && availableFilter === "PICKUP_NOW" ? (
                <SkeletonWave className="pt-8 px-4 z-0" />
              ) : filteredStores?.length !== 0 ? (
                <HomeStoreList
                  filteredStores={filteredStores}
                  attachObserver={attachObserver}
                  loadMoreRef={loadMoreRef}
                  availableFilter={availableFilter}
                  hasMore={hasMore}
                  isLoadingMore={isLoadingMore}
                  onClickStore={onClickStore}
                  onClickLike={onClickLike}
                  onOpenSheet={() => setIsSheetOpen(true)}
                  navigate={navigate}
                />
              ) : (
                <NoStore onClickGoMaps={() => onClickTab("MAP")} />
              )}
            </ScrollRestore>
          )}
        </div>
        {/* 앱 다운로드 바텀시트 올라오는 것 */}
        <AppDownloadBottomSheet
          isOpen={isSheetOpen}
          onClose={handleCloseSheet}
        />

        {/* 첫 방문 바텀시트 */}
        <FirstVisitBottomSheet
          isOpen={showFirstVisitModal}
          onClose={handleCloseFirstVisitModal}
        />

        {/* 신규 매장 알림 모달 */}
        <NewStoreNotificationModal
          isOpen={isNewStoreModalOpen}
          onClose={handleCloseNewStoreModal}
          stores={newStoresForModal}
          onGoToStore={handleGoToNewStore}
        />

        {/* 첫 구매 혜택 선택 모달 */}
        <FirstPurchaseCouponModal
          isOpen={isFirstPurchaseModalOpen}
          onClose={() => setIsFirstPurchaseModalOpen(false)}
          onSelectCoupon={issueFirstPurchaseCoupon}
        />

        {/* 닫기 경고 모달 */}
        <Modal
          isOpen={showCloseWarning}
          onClose={() => {
            markFirstPurchaseSeen();
            setShowCloseWarning(false);
          }}
          onYes={() => {
            setShowCloseWarning(false);
          }}
          yesText="계속 받을게요"
          noText="포기할게요"
          content={
            <div className="flex flex-col items-center justify-center text-center text-gray-600 mb-2">
              <HiExclamationCircle className="w-12 h-12 text-yellow-300 mb-3" />
              <p>지금 창을 닫으면</p>
              <p className="font-bold text-gray-900 mt-1">
                혜택을 다시 받을 수 없게 됩니다.
              </p>
            </div>
          }
        />

        {/* 픽업 시간 필터 바텀시트 */}
        <PickupTimeFilterBottomSheet
          isOpen={isPickupTimeFilterOpen}
          onClose={() => {
            setIsPickupTimeFilterOpen(false);
            handleFilterOpen();
          }}
          onApply={applyFilter}
          onReset={resetFilter}
          hasFilter={hasFilter}
          initialStartTime={initialStartTime}
          initialEndTime={initialEndTime}
        />

        {/* BOTTOM TAB */}
        <BottomTab />

        <img
          className="fixed bottom-0 z-0 w-[42px] h-[42px]"
          src="https://waasrzvnijhadqtenzsq.supabase.co/storage/v1/object/public/product/3x_map-marker.png"
          alt="Custom Marker1"
        />
        <img
          className="fixed bottom-0 z-0 w-[42px] h-[42px]"
          src="https://waasrzvnijhadqtenzsq.supabase.co/storage/v1/object/public/product/marker.png"
          alt="Custom Marker2"
        />
      </div>
    </div>
  );
};

export default Home;
